import React, { useState, useMemo, useEffect } from 'react';
import {
  X,
  Send,
  ChevronDown,
  User,
  Calendar,
  FileText,
  AlertCircle,
  Paperclip,
  Trash2,
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import EmailEditor from './EmailEditor';
import {
  replaceVariables,
  buildVariables,
  sendEmail,
  getGmailStatus,
  fileToEmailAttachment,
} from '../lib/emailService';

/**
 * Email Composer Modal
 * Full compose experience: pick event/client, select template, edit with rich editor, preview, send.
 */
const EmailComposer = ({
  isOpen,
  onClose,
  preselectedEvent = null,
  initialTemplate = null,
  // When opened via Reply / Forward we want to pre-fill without locking the
  // user in — they can still pick a template or change anything.
  initialTo = '',
  initialSubject = '',
  initialBody = '',
  onEmailSent,
}) => {
  const { events, emailTemplates, venues } = useAppContext();

  // Form state
  const [selectedEventId, setSelectedEventId] = useState(preselectedEvent?.id || '');
  const [toEmail, setToEmail] = useState(
    initialTo || preselectedEvent?.clientEmail || preselectedEvent?.email || ''
  );
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState(initialSubject || initialTemplate?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(initialBody || initialTemplate?.body || '');
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplate?.id || '');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showEventPicker, setShowEventPicker] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = React.useRef(null);

  // When preselectedEvent changes (e.g. opening from a different event), update the form
  useEffect(() => {
    if (preselectedEvent) {
      setSelectedEventId(preselectedEvent.id);
      setToEmail((prev) => prev || preselectedEvent.clientEmail || preselectedEvent.email || '');
    }
  }, [preselectedEvent]);

  // Seed To / Subject / Body when opened with Reply / Forward data. Keyed off
  // isOpen so re-opening with fresh initial values updates the form.
  useEffect(() => {
    if (!isOpen) return;
    if (initialTo) setToEmail(initialTo);
    if (initialSubject) setSubject(initialSubject);
    if (initialBody) setBodyHtml(initialBody);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTo, initialSubject, initialBody]);

  const gmailStatus = getGmailStatus();

  const selectedEvent = useMemo(() => {
    return events.find((e) => e.id === selectedEventId) || null;
  }, [events, selectedEventId]);

  const selectedVenue = useMemo(() => {
    if (!selectedEvent) return null;
    return venues.find((v) => v.id === selectedEvent.venue_id) || null;
  }, [selectedEvent, venues]);

  // When event changes, update To field
  const handleEventChange = (eventId) => {
    setSelectedEventId(eventId);
    const event = events.find((e) => e.id === eventId);
    if (event) {
      setToEmail(event.clientEmail || event.email || '');
    }
    setShowEventPicker(false);
  };

  // When template changes, populate subject and body
  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = emailTemplates.find((t) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      // Convert plain text body to basic HTML
      const htmlBody = template.body
        .split('\n')
        .map((line) => (line.trim() ? `<p>${line}</p>` : '<br>'))
        .join('');
      setBodyHtml(htmlBody);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB per file
    const validFiles = files.filter(f => f.size <= maxSize);
    if (validFiles.length < files.length) {
      alert('Some files were too large (max 10MB per file) and were not added.');
    }
    setAttachedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Send the email
  const handleSend = async () => {
    if (!toEmail || !subject) return;

    setSending(true);
    setSendResult(null);

    try {
      // Convert attached files to base64
      const emailAttachments = await Promise.all(
        attachedFiles.map(file => fileToEmailAttachment(file))
      );

      // Replace variables in subject and body.
      // Subject is plain-text email header; body is HTML (default escaped).
      const vars = selectedEvent ? buildVariables(selectedEvent, selectedVenue) : {};
      const finalSubject = replaceVariables(subject, vars, { escape: false });
      const finalBody = replaceVariables(bodyHtml, vars);
      const plainText = finalBody.replace(/<[^>]*>/g, '').trim();

      const result = await sendEmail({
        to: toEmail,
        subject: finalSubject,
        bodyHtml: finalBody,
        bodyText: plainText,
        cc: ccEmail ? ccEmail.split(',').map((e) => e.trim()) : [],
        attachments: emailAttachments,
      });

      setSendResult(result);

      if (result.status === 'sent' && onEmailSent) {
        onEmailSent({
          ...result,
          to: toEmail,
          subject: finalSubject,
          body: finalBody,
          templateId: selectedTemplateId,
          templateName: emailTemplates.find((t) => t.id === selectedTemplateId)?.name || 'Custom',
          eventId: selectedEventId,
          eventName: selectedEvent?.name || '',
          clientName: selectedEvent?.client || toEmail,
          bodyPreview: plainText.substring(0, 120),
          // Persist the token so opens/clicks recorded server-side can be
          // attributed back to this row.
          trackingId: result.trackingToken || '',
          tracking: { opens: 0, clicks: 0, events: [] },
        });
      }
    } catch (error) {
      setSendResult({ status: 'failed', error: error.message });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 820, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Send size={20} className="text-emerald-600" />
            <h2 className="text-lg font-bold text-gray-900">Compose Email</h2>
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${
                gmailStatus.connected
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {gmailStatus.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Event selector */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Linked Event
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEventPicker(!showEventPicker)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm text-left hover:border-gray-400 transition"
                >
                  <span className={selectedEvent ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedEvent
                      ? `${selectedEvent.name} (${selectedEvent.client})`
                      : 'Select an event...'}
                  </span>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>
                {showEventPicker && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedEventId('');
                        setShowEventPicker(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
                    >
                      No event (standalone email)
                    </button>
                    {events
                      .filter((e) => e.clientEmail || e.email)
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((event) => (
                        <button
                          key={event.id}
                          onClick={() => handleEventChange(event.id)}
                          className="block w-full text-left px-3 py-2 hover:bg-emerald-50 transition"
                        >
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">{event.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 ml-6">
                            {event.client} • {new Date(event.date).toLocaleDateString('en-IE')}
                          </p>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Template selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Start from blank</option>
                <optgroup label="Event Templates">
                  {emailTemplates
                    .filter((t) => !t.id.startsWith('wedding_'))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Wedding Templates">
                  {emailTemplates
                    .filter((t) => t.id.startsWith('wedding_'))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          {/* To / CC fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                To
              </label>
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg">
                <User size={14} className="text-gray-400" />
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  className="flex-1 text-sm focus:outline-none text-gray-900 placeholder-gray-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                CC <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
                placeholder="cc@example.com, another@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Rich email editor */}
          <EmailEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            onSubjectChange={setSubject}
            subject={subject}
            previewEvent={selectedEvent}
            previewVenue={selectedVenue}
            minHeight={250}
          />

          {/* Attachments */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Paperclip size={14} className="text-gray-500" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Attachments</span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="ml-auto flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition"
              >
                <Paperclip size={12} />
                Add File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            {attachedFiles.length > 0 && (
              <div className="space-y-1">
                {attachedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-gray-400" />
                      <span className="text-sm text-gray-800">{file.name}</span>
                      <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(idx)}
                      className="p-1 text-gray-400 hover:text-red-500 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {attachedFiles.length === 0 && (
              <p className="text-xs text-gray-400 italic">No files attached</p>
            )}
          </div>

          {/* Send result */}
          {sendResult && (
            <div
              className={`flex items-center gap-3 p-3 rounded-lg ${
                sendResult.status === 'sent'
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {sendResult.status === 'sent' ? (
                <>
                  <Send size={16} />
                  <span className="text-sm font-medium">
                    Email sent successfully{sendResult.demo ? ' (demo mode)' : ''}!
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle size={16} />
                  <span className="text-sm font-medium">
                    Failed to send: {sendResult.error || 'Unknown error'}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            {!gmailStatus.connected && (
              <span className="flex items-center gap-1">
                <AlertCircle size={12} />
                Demo mode: emails are simulated, not actually sent
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !toEmail || !subject}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: sending ? '#6b7280' : '#1A5C3A' }}
            >
              <Send size={16} />
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
